import toast from "react-hot-toast";

/**
 * Centralized logging service for QMS UI
 * Handles error reporting, user notifications, and debug logging
 */
class LogService {
  static LOG_LEVELS = {
    ERROR: "error",
    WARN: "warn",
    INFO: "info",
    DEBUG: "debug",
  };

  static instance = null;

  constructor() {
    if (LogService.instance) {
      return LogService.instance;
    }
    LogService.instance = this;

    // Configure log level based on environment
    this.logLevel =
      process.env.NODE_ENV === "development"
        ? LogService.LOG_LEVELS.DEBUG
        : LogService.LOG_LEVELS.ERROR;
  }

  /**
   * Log an error with user notification
   * @param {Error|string} error - Error object or message
   * @param {string} userMessage - User-friendly message to display
   * @param {Object} context - Additional context for debugging
   */
  error(error, userMessage = "An error occurred", context = {}) {
    const errorObj = error instanceof Error ? error : new Error(error);

    // Log error details
    console.error("Error:", {
      message: errorObj.message,
      stack: errorObj.stack,
      context,
    });

    // Show user notification
    toast.error(userMessage);

    // Additional error reporting could be added here
    // e.g., Sentry, LogRocket, etc.
  }

  /**
   * Log a warning with optional user notification
   * @param {string} message - Warning message
   * @param {boolean} notify - Whether to show user notification
   * @param {Object} context - Additional context
   */
  warn(message, notify = false, context = {}) {
    if (this.shouldLog(LogService.LOG_LEVELS.WARN)) {
      console.warn("Warning:", { message, context });
      if (notify) {
        toast.warning(message);
      }
    }
  }

  /**
   * Log info message with optional user notification
   * @param {string} message - Info message
   * @param {boolean} notify - Whether to show user notification
   * @param {Object} context - Additional context
   */
  info(message, notify = false, context = {}) {
    if (this.shouldLog(LogService.LOG_LEVELS.INFO)) {
      console.info("Info:", { message, context });
      if (notify) {
        toast.success(message);
      }
    }
  }

  /**
   * Log debug message (development only)
   * @param {string} message - Debug message
   * @param {Object} context - Additional context
   */
  debug(message, context = {}) {
    if (this.shouldLog(LogService.LOG_LEVELS.DEBUG)) {
      console.debug("Debug:", { message, context });
    }
  }

  /**
   * Check if message should be logged based on current log level
   * @param {string} level - Log level to check
   * @returns {boolean}
   */
  shouldLog(level) {
    const levels = Object.values(LogService.LOG_LEVELS);
    const currentIndex = levels.indexOf(this.logLevel);
    const targetIndex = levels.indexOf(level);
    return targetIndex <= currentIndex;
  }

  /**
   * Handle API error responses
   * @param {Error} error - Axios error object
   * @param {string} defaultMessage - Default user message
   */
  handleApiError(
    error,
    defaultMessage = "An error occurred while processing your request",
  ) {
    let userMessage = defaultMessage;

    if (error.response) {
      // Server responded with error
      const { status, data } = error.response;

      switch (status) {
        case 400:
          userMessage =
            data.message || "Invalid request. Please check your input.";
          break;
        case 401:
          userMessage = "Your session has expired. Please log in again.";
          break;
        case 403:
          userMessage = "You do not have permission to perform this action.";
          break;
        case 404:
          userMessage = "The requested resource was not found.";
          break;
        case 422:
          userMessage =
            data.message || "Validation error. Please check your input.";
          break;
        case 500:
          userMessage =
            "An internal server error occurred. Please try again later.";
          break;
        default:
          userMessage = data.message || defaultMessage;
      }
    } else if (error.request) {
      // Request made but no response
      userMessage =
        "Unable to connect to the server. Please check your internet connection.";
    }

    this.error(error, userMessage, {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
    });
  }
}

// Export singleton instance
export const logger = new LogService();
